import type { CloudflareOverview, DashboardData, GrowthOverview, VideoGrowthOverview } from "./types";

export const fallbackDashboard: DashboardData = {
  metrics: [
    {
      label: "英文机会",
      value: "6",
      detail: "AI 工具 + 市场热门攻略队列"
    },
    {
      label: "公开站点",
      value: "3",
      detail: "Pricing Lab + AI Video Factory + Hot Guides"
    },
    {
      label: "免费资源栈",
      value: "$0",
      detail: "Pages + D1 + KV + 私有 R2 已接入"
    },
    {
      label: "90 天目标",
      value: "$500 MRR",
      detail: "或 3 个合格 B2B 服务线索"
    }
  ],
  opportunities: [
    {
      id: "opp_hot_tool_game_guides",
      title: "Hot Tool and Game Guides",
      niche: "tool-game-guides",
      language: "en",
      primary_keyword: "hot tools and game guides",
      audience: "English-speaking gamers, AI tool users, parents and beginners",
      problem:
        "Readers need current, safe and visual guidance for trending tools and games without fake generators, risky downloads or stale lists.",
      monetization:
        "AdSense, affiliate software/game deals, newsletter capture after proven traffic, related guide clusters",
      score: 31,
      status: "shortlisted",
      evidence_url: "manual-market-research-2026-06-12",
      notes:
        "External-market guide site covering Roblox, Steam and AI tools. This is not own-product documentation."
    },
    {
      id: "opp_ai_video_factory",
      title: "AI Short Video Factory for Solo Founders",
      niche: "ai-video-tools",
      language: "en",
      primary_keyword: "ai short video generator for founders",
      audience: "English-speaking solo founders, B2B creators and small agencies",
      problem:
        "Creators need to turn ideas into hooks, scripts, scenes, captions and asset searches without hiring a video strategist.",
      monetization:
        "Freemium packet generator, paid credits, brand voice templates, managed short-video production service",
      score: 30,
      status: "shortlisted",
      evidence_url: "https://github.com/harry0703/MoneyPrinterTurbo",
      notes:
        "Inspired by MoneyPrinterTurbo workflow, implemented as a Cloudflare-native production-packet MVP before server-side video rendering."
    },
    {
      id: "opp_ai_tool_workflow_guides",
      title: "AI Tool Workflow Guides for Creators",
      niche: "ai-tool-guides",
      language: "en",
      primary_keyword: "ai video workflow tutorial",
      audience: "English-speaking creators, operators and beginners",
      problem:
        "Beginners want step-by-step workflows, setup notes, risk boundaries and tool comparisons before paying for AI products.",
      monetization: "AdSense, affiliate links, downloadable checklists, email capture and paid templates",
      score: 29,
      status: "research",
      evidence_url: "manual-research",
      notes: "Guide/tutorial content supports SEO while the AI tool track validates paid intent."
    },
    {
      id: "opp_saas_pricing_calculator",
      title: "SaaS Pricing Calculator",
      niche: "saas-operators",
      language: "en",
      primary_keyword: "saas pricing calculator",
      audience: "Bootstrapped SaaS founders and indie hackers",
      problem: "Founders need a quick way to model pricing, margin, churn and payback.",
      monetization: "Paid spreadsheet, affiliate tools, consulting lead",
      score: 31,
      status: "shortlisted",
      evidence_url: "manual-research",
      notes: "High commercial intent and a narrow founder audience."
    },
    {
      id: "opp_youtube_title_generator",
      title: "YouTube Title Generator for B2B Creators",
      niche: "creator-tools",
      language: "en",
      primary_keyword: "youtube title generator for business videos",
      audience: "English-speaking solo creators and B2B marketers",
      problem: "Creators need sharper titles quickly without hiring a copywriter.",
      monetization: "Template pack, affiliate tools, premium prompt set",
      score: 28,
      status: "shortlisted",
      evidence_url: "manual-research",
      notes: "Low compliance risk and easy MVP."
    },
    {
      id: "opp_cold_email_rewriter",
      title: "Cold Email Rewriter for Agencies",
      niche: "sales-enablement",
      language: "en",
      primary_keyword: "cold email rewriter",
      audience: "Small agencies, SDRs and freelancers",
      problem: "Users need to improve outbound emails while avoiding spammy copy.",
      monetization: "Freemium tool, prompt pack, agency lead gen",
      score: 27,
      status: "research",
      evidence_url: "manual-research",
      notes: "Competitive but easy to validate with examples."
    }
  ],
  sites: [
    {
      id: "site_hot_guides",
      name: "Hot Guides",
      slug: "hot-guides",
      domain: "https://tools.kakijio.com/hot-guides/",
      type: "guide",
      status: "live",
      niche: "tool-game-guides",
      primary_keyword: "hot tools and game guides",
      monetization: "AdSense + affiliate links after traffic validation + related guide navigation",
      pages_project: "ai-siteops-cloudflare",
      repository_url: "https://github.com/cndingbo2030/ai-siteops-cloudflare",
      analytics_tag: null
    },
    {
      id: "site_pricing_lab",
      name: "Pricing Lab Tools",
      slug: "pricing-lab-tools",
      domain: "https://tools.kakijio.com/pricing-lab-tools/",
      type: "calculator",
      status: "live",
      niche: "saas-operators",
      primary_keyword: "saas pricing calculator",
      monetization: "template pack + founder consulting lead",
      pages_project: null,
      repository_url: "https://github.com/cndingbo2030/ai-siteops-cloudflare",
      analytics_tag: null
    },
    {
      id: "site_ai_video_factory",
      name: "AI Video Factory",
      slug: "ai-video-factory",
      domain: "https://tools.kakijio.com/ai-video-factory/",
      type: "generator",
      status: "live",
      niche: "ai-video-tools",
      primary_keyword: "ai short video generator for founders",
      monetization: "freemium production packets + future credits + managed service lead",
      pages_project: "ai-siteops-cloudflare",
      repository_url: "https://github.com/cndingbo2030/ai-siteops-cloudflare",
      analytics_tag: null
    }
  ],
  tasks: [
    {
      id: "task_hot_guides_mvp_launch",
      title: "上线 Hot Guides 市场热门攻略站 MVP",
      area: "攻略/教程站",
      priority: "high",
      status: "done",
      site_id: "site_hot_guides",
      due_date: null,
      notes: "首批覆盖 Roblox、Steam 和 AI 工具教程；使用生成教程图和来源链接。"
    },
    {
      id: "task_hot_guides_daily_refresh",
      title: "建立 Roblox code guide 每日刷新流程",
      area: "内容更新",
      priority: "high",
      status: "todo",
      site_id: "site_hot_guides",
      due_date: null,
      notes: "每天检查 Grow a Garden 和 Roblox code source；过期内容不继续铺量。"
    },
    {
      id: "task_setup_cloudflare_resources",
      title: "完成 Cloudflare Pages、D1、KV、R2",
      area: "基础设施",
      priority: "high",
      status: "done",
      site_id: null,
      due_date: null,
      notes: "wrangler.toml 已写入真实 Pages、D1、KV、R2 配置；siteops-assets 用于私有生产资产。"
    },
    {
      id: "task_select_first_three_niches",
      title: "执行 Pricing Lab 首周英文留言引流",
      area: "增长",
      priority: "high",
      status: "doing",
      site_id: null,
      due_date: null,
      notes: "只做高质量人工回复；记录每条渠道 URL、UTM 和线索结果。"
    },
    {
      id: "task_ai_video_factory_launch",
      title: "上线 AI Video Factory 并验证英文工具流量",
      area: "AI 工具",
      priority: "high",
      status: "doing",
      site_id: "site_ai_video_factory",
      due_date: null,
      notes: "先验证生产包生成、D1 写入、英文渠道点击和邮件/服务意向。"
    },
    {
      id: "task_ai_tool_guide_cluster",
      title: "建设 AI 视频工具教程内容集群",
      area: "SEO",
      priority: "high",
      status: "todo",
      site_id: "site_ai_video_factory",
      due_date: null,
      notes: "围绕 AI video workflow tutorial 等关键词写 10 个英文教程页。"
    },
    {
      id: "task_enable_r2_media_assets",
      title: "启用 R2 存储视频项目资产",
      area: "Cloudflare 付费资源",
      priority: "medium",
      status: "done",
      site_id: "site_ai_video_factory",
      due_date: null,
      notes: "AI Video Factory 生成包 JSON 写入私有 R2；后续再评估 transcript、subtitle、audio、thumbnail。"
    },
    {
      id: "task_enable_stream_video_hosting",
      title: "评估启用 Cloudflare Stream 承载视频上传和播放",
      area: "Cloudflare 付费资源",
      priority: "medium",
      status: "todo",
      site_id: "site_ai_video_factory",
      due_date: null,
      notes: "只有当用户需要真实视频上传/预览时启用；先测算存储分钟和播放分钟。"
    },
    {
      id: "task_enable_containers_ffmpeg",
      title: "评估 Cloudflare Containers 执行 ffmpeg 渲染",
      area: "Cloudflare 付费资源",
      priority: "low",
      status: "todo",
      site_id: "site_ai_video_factory",
      due_date: null,
      notes: "只有当浏览器渲染或第三方服务不能满足需求时，才考虑 Containers。"
    }
  ],
  weeklyFocus: [
    "今天新增市场热门攻略站：先验证 Roblox 游戏攻略和 AI 工具教程，不再只写自有产品教程。",
    "Pricing Lab 继续验证 B2B 高意图；AI Video Factory 验证 AI 工具付费意愿。",
    "教程/攻略内容负责 SEO 和社媒入口，AI 工具负责转化和未来点数/会员。"
  ]
};

export const fallbackCloudflareOverview: CloudflareOverview = {
  accountId: "8b9c797605bf3f96bd5c7bcf8af33dd7",
  adminDomain: "admin-db.kakijio.com",
  apiTokenConfigured: false,
  automations: [
    {
      action: "sync-siteops-metadata",
      description: "把后台域名、公开工具域名、部署记录和执行任务同步到管理数据库。",
      enabled: true,
      label: "同步当前站点元数据到 D1",
      mode: "safe-write"
    },
    {
      action: "queue-cloudflare-token",
      description: "生成最小权限 token 配置任务；配置后后台可读取 Cloudflare 真实资源。",
      enabled: true,
      label: "创建 Cloudflare API Token 配置任务",
      mode: "queued"
    },
    {
      action: "queue-turnstile",
      description: "复核 Pricing Lab 和 AI Video Factory 的 lead form 防护，降低 bot 和垃圾线索风险。",
      enabled: true,
      label: "复核 Turnstile 表单防护",
      mode: "queued"
    },
    {
      action: "queue-web-analytics",
      description: "把 tools.kakijio.com 的访问来源、页面和转化接入周报。",
      enabled: true,
      label: "创建 Web Analytics 接入任务",
      mode: "queued"
    },
    {
      action: "queue-workers-ai-video-factory",
      description: "为 AI Video Factory 评估从 template 模式切换到 Workers AI 的成本和质量。",
      enabled: true,
      label: "创建 Workers AI 视频工厂配置任务",
      mode: "queued"
    },
    {
      action: "verify-r2-video-storage",
      description: "向 siteops-assets 写入健康检查文件，并登记 AI Video Factory 生成包的 R2 存储状态。",
      enabled: true,
      label: "校验 R2 视频资产存储",
      mode: "safe-write"
    }
  ],
  generatedAt: new Date(0).toISOString(),
  publicToolsDomain: "tools.kakijio.com",
  resources: [
    {
      count: null,
      enabled: false,
      error: "本地预览未连接 Pages Functions；线上通过 /api/cloudflare 读取。",
      id: "pages-projects",
      label: "Pages 项目",
      method: "GET",
      ok: false,
      path: "/accounts/{account_id}/pages/projects",
      requires: ["CLOUDFLARE_ACCOUNT_ID", "CLOUDFLARE_API_TOKEN"],
      sample: [],
      status: null
    },
    {
      count: null,
      enabled: false,
      error: "配置 Cloudflare API Token 后启用。",
      id: "workers-services",
      label: "Workers 服务",
      method: "GET",
      ok: false,
      path: "/accounts/{account_id}/workers/services",
      requires: ["CLOUDFLARE_ACCOUNT_ID", "CLOUDFLARE_API_TOKEN"],
      sample: [],
      status: null
    },
    {
      count: null,
      enabled: false,
      error: "配置 Cloudflare API Token 后启用。",
      id: "d1-databases",
      label: "D1 数据库",
      method: "GET",
      ok: false,
      path: "/accounts/{account_id}/d1/database",
      requires: ["CLOUDFLARE_ACCOUNT_ID", "CLOUDFLARE_API_TOKEN"],
      sample: [],
      status: null
    },
    {
      count: null,
      enabled: false,
      error: "配置 Cloudflare API Token 后启用。",
      id: "r2-buckets",
      label: "R2 Buckets",
      method: "GET",
      ok: false,
      path: "/accounts/{account_id}/r2/buckets",
      requires: ["CLOUDFLARE_ACCOUNT_ID", "CLOUDFLARE_API_TOKEN"],
      sample: [],
      status: null
    },
    {
      count: null,
      enabled: false,
      error: "线上 Pages Functions 会显示真实 SITEOPS_R2 绑定状态。",
      id: "r2-binding",
      label: "R2 运行时绑定",
      method: "GET",
      ok: false,
      path: "SITEOPS_R2 -> siteops-assets",
      requires: ["SITEOPS_R2"],
      sample: [],
      status: null
    },
    {
      count: null,
      enabled: false,
      error: "配置 Cloudflare API Token 后启用。",
      id: "dns-records",
      label: "DNS 记录",
      method: "GET",
      ok: false,
      path: "/zones/{zone_id}/dns_records?per_page=100",
      requires: ["CLOUDFLARE_ZONE_ID", "CLOUDFLARE_API_TOKEN"],
      sample: [],
      status: null
    }
  ],
  zoneId: "f587503eef1ebbc2098fa9bb5b1ca7ef"
};

export const fallbackGrowthOverview: GrowthOverview = {
  actions: [
    {
      action_type: "comment",
      channel: "Indie Hackers",
      created_at: new Date(0).toISOString(),
      id: "growth_seed_indiehackers",
      leads: 0,
      message_angle: "Request blunt feedback from SaaS founders validating pricing pages.",
      notes: "本地占位：线上通过 /api/growth 读取 D1。",
      post_url: null,
      published_at: null,
      site_id: "site_pricing_lab",
      status: "planned",
      target_url:
        "https://tools.kakijio.com/pricing-lab-tools/?utm_source=indiehackers&utm_medium=community&utm_campaign=pricing_lab_launch",
      updated_at: new Date(0).toISOString(),
      utm_campaign: "pricing_lab_launch",
      utm_medium: "community",
      utm_source: "indiehackers",
      visits: 0
    }
  ],
  channels: [
    {
      actions: 1,
      channel: "Indie Hackers",
      leads: 0,
      source: "indiehackers",
      visits: 0
    }
  ],
  generatedAt: new Date(0).toISOString(),
  metrics: [
    { label: "本周动作", value: "0", detail: "等待第一条真实英文外部评论记录" },
    { label: "已发布", value: "0", detail: "只记录人工发布或人工审核后的动作" },
    { label: "访问", value: "0", detail: "先人工回填，后续接 Analytics" },
    { label: "线索率", value: "0%", detail: "线索 / 访问；低于 10% 先改页面" }
  ],
  stopLine:
    "连续 100 个合格访问仍少于 10 个表单线索时，停止继续铺渠道，先重写定位、CTA 和页面首屏。"
};

export const fallbackVideoGrowth: VideoGrowthOverview = {
  funnel: [
    {
      count: 0,
      event_name: "video_page_view",
      last_seen: "-"
    },
    {
      count: 0,
      event_name: "video_generate_submit",
      last_seen: "-"
    },
    {
      count: 0,
      event_name: "video_lead_success",
      last_seen: "-"
    }
  ],
  generatedAt: new Date(0).toISOString(),
  metrics: [
    { label: "事件", value: "0", detail: "等待线上事件写入 D1" },
    { label: "生成", value: "0", detail: "production packet 生成次数" },
    { label: "线索", value: "0", detail: "留下邮箱和需求的用户" },
    { label: "线索率", value: "0%", detail: "线索数 / 已记录事件" }
  ],
  recentLeads: [],
  recentProjects: [],
  sources: [
    {
      events: 0,
      generated: 0,
      leads: 0,
      source: "direct"
    }
  ]
};
